"use client";

import { ITopLeagueFeedResponse, ITournamentFeedResponse } from "@finbackoffice/websocket-client";
import { SportFavoritesContext, FeedRidContext, useCollapseMargin } from "@finbackoffice/site-core";
import classNames from "classnames";
import { FC, useContext, useMemo } from "react";
import { useSocketStorage } from "hooks";
import { Svg } from "components/base/svg/Svg";
import SportIcon from "components/base/sport-icon/SportIcon";
import { AssetsDirectory } from "@finbackoffice/enums";
import { LiveCategoriesVariantType } from "../layouts/categories/LiveCategories";
import LiveGamesContainer from "./game/LiveGamesContainer";
import styles from "./live-tournament.module.sass";
import { LiveTournamentGameLayoutEnum } from "./game/LiveTournamentGame";

type IProps = {
    tournament: ITournamentFeedResponse;
    sportId: number;
    openedByDefault?: boolean;
    eventIds?: number[];
    showArrow?: boolean;
    gameLayout?: LiveTournamentGameLayoutEnum;
    feedRid?: string;
    isTwoColumns?: boolean;
    variant?: LiveCategoriesVariantType;
};

const LiveTournament: FC<IProps> = ({
    tournament,
    sportId,
    openedByDefault = false,
    showArrow = true,
    feedRid,
    gameLayout,
    eventIds,
    variant,
    isTwoColumns,
}) => {
    const { liveRid } = useContext(FeedRidContext);
    const { getEvents } = useSocketStorage(feedRid || liveRid);
    const { isFav } = useContext(SportFavoritesContext);
    const { opened, animating, headerRef, containerRef } = useCollapseMargin(openedByDefault);
    const events = getEvents(tournament.id);

    const games = useMemo(() => {
        if (eventIds === undefined) return events;

        const eventIdsSet = new Set(eventIds);
        return events.filter((event) => eventIdsSet.has(event.id));
    }, [eventIds, events]);

    return (
        <div className={styles.tournament}>
            <div className={classNames(styles.header, { [styles.opened]: opened })} ref={headerRef}>
                {showArrow && <i className={styles.arrow} />}
                <SportIcon sportId={sportId} />
                <span className={styles.tournamentName}>
                    {(tournament as ITopLeagueFeedResponse).category_name &&
                        `${(tournament as ITopLeagueFeedResponse).category_name} / `}
                    <span>{tournament.name}</span>
                </span>
                {tournament.is_top ? (
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/top-leagues.svg`}
                        className="svg-icon"
                    />
                ) : (
                    tournament.events.some(isFav) && (
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/favourites.svg`}
                            className={classNames("svg-icon", styles.favorites)}
                        />
                    )
                )}
                <span>{tournament.games_count}</span>
            </div>
            {(opened || animating) && (
                <LiveGamesContainer
                    games={games}
                    ref={containerRef}
                    gameLayout={gameLayout}
                    isTwoColumns={isTwoColumns || false}
                    variant={variant}
                />
            )}
        </div>
    );
};

export default LiveTournament;
